
import { FastingProtocol } from '@/types/fasting';
import { loadFastingStateFromStorage, loadFastingStateFromCookies } from '@/utils/persistenceUtils';
import { hasCookieConsent } from '@/utils/cookies';

/**
 * Loads fasting state from available storage options with improved priority handling
 * @param fastingProtocols List of available fasting protocols to match with saved protocol IDs
 * @returns The loaded fasting state or null if none found
 */
export const loadFastingState = (fastingProtocols: FastingProtocol[]) => {
  console.log("[LoadState] Starting state loading process...");
  
  // Strategy 1: Try localStorage first (always available, no consent needed)
  const tryLocalStorage = () => {
    try {
      console.log("[LoadState] Attempting to load from localStorage...");
      const localState = loadFastingStateFromStorage();
        if (localState) {
        console.log("[LoadState] Found valid state in localStorage:", {
          isActive: localState.isActive,
          hasStartTime: !!localState.startTime,
          timeElapsed: localState.timeElapsed,
          protocol: localState.selectedProtocol?.name || localState.selectedProtocolId
        });
        
        // Check if timer should have completed while away
        if (localState.isActive && localState.startTime && localState.selectedProtocol) {
          const now = Date.now();
          const elapsed = Math.floor(((now - localState.startTime) - (localState.totalPauseDuration || 0)) / 1000);
          const targetDuration = localState.selectedProtocol.durationHours * 60 * 60;
          
          if (elapsed >= targetDuration) {
            // Timer completed while away
            localState.isActive = false;
            localState.timeElapsed = targetDuration;
            console.log("[LoadState] Timer completed while away, marking as inactive");
          }
        }
        
        // Ensure we have a valid protocol
        if (!localState.selectedProtocol && localState.selectedProtocolId) {
          const protocol = fastingProtocols.find(p => p.id === localState.selectedProtocolId);
          if (protocol) {
            localState.selectedProtocol = protocol;
          }
        }
        
        return localState;
      }
      
      console.log("[LoadState] No valid state found in localStorage");
      return null;
    } catch (error) {
      console.error("[LoadState] Error loading from localStorage:", error);
      return null;
    }
  };

  // Strategy 2: Try cookies if localStorage fails and consent is given
  const tryCookies = () => {
    try {
      if (!hasCookieConsent()) {
        console.log("[LoadState] No cookie consent, skipping cookie loading");
        return null;
      }
      
      console.log("[LoadState] Attempting to load from cookies...");
      const cookieState = loadFastingStateFromCookies(fastingProtocols);
        if (cookieState && typeof cookieState.isActive === 'boolean') {
        console.log("[LoadState] Found valid state in cookies:", {
          isActive: cookieState.isActive,
          hasStartTime: !!cookieState.startTime,
          timeElapsed: cookieState.timeElapsed,
          protocol: cookieState.selectedProtocol?.name
        });
        
        // Check if timer should have completed while away
        if (cookieState.isActive && cookieState.startTime && cookieState.selectedProtocol) {
          const now = Date.now();
          const elapsed = Math.floor(((now - cookieState.startTime) - (cookieState.totalPauseDuration || 0)) / 1000);
          const targetDuration = cookieState.selectedProtocol.durationHours * 60 * 60;
          
          if (elapsed >= targetDuration) {
            // Timer completed while away
            cookieState.isActive = false;
            cookieState.timeElapsed = targetDuration;
            console.log("[LoadState] Timer completed while away (from cookies), marking as inactive");
          }
        }
        
        return cookieState;
      }
      
      console.log("[LoadState] No valid state found in cookies");
      return null;
    } catch (error) {
      console.error("[LoadState] Error loading from cookies:", error);
      return null;
    }
  };
  
  // Execute loading strategies in order of priority
  const loadedState = tryLocalStorage() || tryCookies();
  
  if (loadedState) {
    console.log("[LoadState] Successfully loaded state from storage");
    return loadedState;
  }
  
  console.log("[LoadState] No saved state found in any storage location");
  return null;
};
